/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import {
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Box,
  Skeleton,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Group as GroupIcon,
  Schedule as ScheduleIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { StudioListCardProps } from '../../types/studioList';
import { StudioRole, SubscriptionStatus } from '../../types/studio';

// Role color mapping
const getRoleColor = (
  role: StudioRole
): 'primary' | 'secondary' | 'success' | 'warning' => {
  switch (role) {
    case StudioRole.OWNER:
      return 'primary';
    case StudioRole.MANAGER:
      return 'secondary';
    case StudioRole.ADMIN:
      return 'success';
    case StudioRole.MEMBER:
      return 'warning';
    default:
      return 'warning';
  }
};

// Subscription status color mapping
const getSubscriptionColor = (
  status: SubscriptionStatus | undefined | null
): 'success' | 'warning' | 'error' | 'default' => {
  if (!status) {
    return 'default';
  }
  switch (status) {
    case SubscriptionStatus.ACTIVE:
      return 'success';
    case SubscriptionStatus.TRIAL:
      return 'warning';
    case SubscriptionStatus.EXPIRED:
    case SubscriptionStatus.CANCELLED:
      return 'error';
    case SubscriptionStatus.PAST_DUE:
      return 'warning';
    default:
      return 'default';
  }
};

export const StudioListCard: React.FC<StudioListCardProps> = ({
  studio,
  onView,
  loading = false,
}) => {
  const { t } = useTranslation('studio');

  // Format role for display using translations
  const formatRole = (role: StudioRole): string => {
    const roleKey = role.toLowerCase() as
      | 'owner'
      | 'manager'
      | 'admin'
      | 'member';
    return t(`roles.${roleKey}`);
  };

  // Format subscription status for display using translations
  const formatSubscriptionStatus = (
    status: SubscriptionStatus | undefined | null
  ): string => {
    if (!status) {
      return t('subscriptionStatus.unknown');
    }
    const statusKey = status.toLowerCase().replace('_', '') as
      | 'active'
      | 'trial'
      | 'expired'
      | 'cancelled'
      | 'pastdue';
    return t(`subscriptionStatus.${statusKey}`);
  };

  // Format date for display
  const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) {
      return t('list.card.noDate');
    }

    const date = new Date(dateString);

    // Check if the date is valid
    if (Number.isNaN(date.getTime())) {
      return t('list.card.invalidDate');
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleCardClick = () => {
    onView(studio.id);
  };

  const handleDashboardClick = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click when clicking the button
    onView(studio.id);
  };

  if (loading) {
    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ ml: 2, flexGrow: 1 }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="text" width="40%" height={20} />
            </Box>
            <Skeleton variant="circular" width={32} height={32} />
          </Box>
          <Skeleton variant="text" width="100%" height={20} />
          <Skeleton variant="text" width="80%" height={20} />
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Skeleton variant="rounded" width={60} height={24} />
            <Skeleton variant="rounded" width={80} height={24} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
      }}
      onClick={handleCardClick}
      data-testid={`studio-card-${studio.id}`}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Studio Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            src={studio.logo}
            alt={`${studio.name} logo`}
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'primary.main',
              fontSize: '1rem',
            }}
          >
            {studio.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ ml: 2, flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              component="h3"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {studio.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {studio.isPersonal
                ? t('list.card.personalStudio')
                : t('list.card.sharedStudio')}
            </Typography>
          </Box>
          <Tooltip title={t('list.card.openDashboard')} arrow>
            <IconButton
              onClick={handleDashboardClick}
              size="small"
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                ml: 1,
              }}
              aria-label={t('list.card.openStudioDashboard')}
            >
              <DashboardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Studio Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: '2.5em',
          }}
        >
          {studio.description || t('list.card.noDescription')}
        </Typography>

        {/* Studio Stats */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <GroupIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {studio.memberCount}{' '}
              {studio.memberCount === 1
                ? t('list.card.member')
                : t('list.card.members')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ScheduleIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {formatDate(studio.lastActivity)}
            </Typography>
          </Box>
        </Box>

        {/* Status Chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={formatRole(studio.userRole)}
            color={getRoleColor(studio.userRole)}
            size="small"
            variant="outlined"
          />
          <Chip
            label={formatSubscriptionStatus(studio.subscriptionStatus)}
            color={getSubscriptionColor(studio.subscriptionStatus)}
            size="small"
            variant="filled"
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default StudioListCard;
